//
// twitter-typeahead.js
//
// Copyright (c) 2016 by Curalate, Inc.
//

import _ from 'js/underscore-mixins';

const DEFAULT_MIN_SEARCH_LENGTH = 5;
const keyCodeMap = {
  9: 'tab',
  27: 'esc',
  37: 'left',
  39: 'right',
  13: 'enter',
  38: 'up',
  40: 'down',
};

  /**
   * twitterTypeahead
   *
   * Comprehensive wrapper directive around Twitter's jQuery typeahead plugin
   *
   * Scope:
   *  getData:    An Angular expression which returns a Promise which resolves to an array of "results"
   *              Note: The typeahead keeps a reference to the current set of results, and if the expression evaluates to "undefined", it will use those results
   *              This was done in an effort to support "debouncing" and/or "throttling" of requests
   *  templates:  An object which contains information for how to retrieve/render templates.
   *              The template object uses template-name as the key ["header", "footer", "empty", "suggestion"]. These keys point to another object
   *              which contains the template information. For instance:
   *              { "url": "/path/to/html/template" }
   *              or
   *              { "html" : "<div>{{ arbitrary Angular expression }}</div>" }
   *              Example:
   *                { suggestion: { url: "/partial/url" }, footer: { html: "<div>{{ expression }}</div>" }
   *  key:        String or Function describing the value of a particular expression. See "displayKey" here: https://github.com/twitter/typeahead.js/blob/master/doc/jquery_typeahead.md#datasets
   *  context:    Object containing values that will be accessible in the scope in which the "suggestion" template is rendered
   *              This is used to pass arbitrary values or out-of-scope functions into the template's isolate scope
   *              A particular example would be to pass a function used as an ng-click callback. See locations.js for an example of this
   */
angular.module('curalate')
      .constant('twitterTypeahead.templateDefaults', {
        header: '',
        footer: '',
        empty: '',
        suggestion: '<p>{{ name }}</p>',
      })
      .directive('twitterTypeahead', [
        '$rootScope',
        '$compile',
        '$interpolate',
        '$timeout',
        '$http',
        '$q',
        'twitterTypeahead.templateDefaults',
        'TrackingService',
        function(
          $rootScope,
          $compile,
          $interpolate,
          $timeout,
          $http,
          $q,
          templateDefaults,
          TrackingService,
        ) {
          return {
            restrict: 'AC',
            transclude: true,
            scope: {
              getData: '=',
              templates: '=',
              key: '=',
              events: '=?',
              context: '=',
              trackCategory: '@',
              trackActionPrefix: '@?',
              fixedScrollBind: '@?',
            },
            link(scope, element, attrs) {
              const minLength = attrs.minLength || DEFAULT_MIN_SEARCH_LENGTH;
              const bindTypeahead = function(templates) {
                const linkedTemplates = {};
                let rendered = {};
                let lastQuery;
                let lastCursorItem;
                const actionPrefix = scope.trackActionPrefix ? `${scope.trackActionPrefix}:` : '';

                // Combine default templates with ones passed into the function,
                // compile all of them and return an map
                const compiledTemplates = _.chain(templateDefaults)
                          .map((value, key) => {
                            const obj = {};
                            const existing = _(templates).findWhere({ type: key });
                            obj[key] = (existing && existing.template) ? $compile(existing.template) : $compile(value);
                            return obj;
                          })
                          .reduce((memo, value) => { return _.extend(memo, value); }, {})
                          .value();

                function generateHtmlFunc(where) {
                  const ele = angular.element(where);

                  // Return DOM if we have it in order to preserve events
                  return ele.length > 0 ? ele[0] : '';
                }

                const typeaheadElem = angular.element(element).typeahead({
                  minLength,
                  highlight: true,
                }, {
                  // Reimplement this crap as a Bloodhound dataset
                  source(query, cb, asynccb) {
                    const deferred = $q.defer();

                    lastQuery = query;

                    // Call the custom "getData" function and pass the deferred to be resolved
                    scope.getData(query, deferred);

                    // If the specified returns undefined, the last set of results is used
                    // This logic covers the case of callbacks which are wrapped in _.debounce or _.throttle calls
                    deferred.promise.then((results) => {
                      // Only hit TrackingService if the custom getData function resolved the deferred we passed
                      TrackingService.recordEvent(scope.trackCategory, `${actionPrefix}TypeaheadSearch`, query);

                      // Destroy old scopes from previously rendered items
                      _.chain(rendered)
                                      .map((dom) => { return angular.element(dom).scope(); })
                                      .compact()
                                      .each((scop) => { scop.$destroy(); });

                      rendered = {};

                      // Update scope variables with current state so templates have access to them
                      scope.query = query;
                      scope.isEmpty = results.length === 0;
                      scope.resultTotal = results.length;

                      // Re-link the three "static" templates with the new state
                      linkedTemplates.header = compiledTemplates.header(scope, angular.noop);
                      linkedTemplates.footer = compiledTemplates.footer(scope, angular.noop);
                      linkedTemplates.empty = compiledTemplates.empty(scope, angular.noop);

                      // Link each result against the template
                      if (results.length > 0) {
                        _(results).each((result, i) => {
                          const newScope = scope.$new(true);
                          // eslint-disable-next-line no-underscore-dangle
                          result._scope_id = newScope.$id;
                          newScope.data = result;
                          newScope.data.index = i;
                          newScope.context = scope.context;
                          newScope.isEmpty = scope.isEmpty;
                          newScope.query = query;

                          // Pass the noop function to get element cloning only
                          // eslint-disable-next-line no-underscore-dangle
                          rendered[result._scope_id] = compiledTemplates.suggestion(newScope, angular.noop);
                        });
                      }

                      // Wait until the current $digest loop finishes to call the typeahead callback
                      // so the data bindings for the templates have a chance to update
                      $timeout(() => {
                        if (_.isFunction(asynccb)) {
                          asynccb(results);
                        } else {
                          cb(results);
                        }
                      });
                    });
                  },
                  displayKey(obj) {
                    if (angular.isFunction(scope.key)) {
                      return scope.key.call(this, obj);
                    } else {
                      return obj[scope.key];
                    }
                  },
                  templates: {
                    header() {
                      return generateHtmlFunc(linkedTemplates.header);
                    },
                    footer() {
                      return generateHtmlFunc(linkedTemplates.footer);
                    },
                    empty() {
                      return generateHtmlFunc(linkedTemplates.empty);
                    },
                    suggestion(obj) {
                      // eslint-disable-next-line no-underscore-dangle
                      return generateHtmlFunc(rendered[obj._scope_id]);
                    },
                  },
                });

                      // Tie into typeahead custom events
                scope.events = scope.events || {};

                _.chain(scope.events).keys().each((key) => {
                  typeaheadElem.on(`typeahead:${key}`, (...args) => {
                    scope.$apply(() => {
                      scope.events[key].apply(typeaheadElem, [].slice.call(args, 0));
                      TrackingService.recordEvent(scope.trackCategory, `${actionPrefix}TypeaheadInteraction`, key);
                    });
                  });
                });

                      // Keep track to item cursor points to in order to enrich DOM events below
                      // NOTE: cursor change events are only fired for keyboard navigation
                typeaheadElem.on('typeahead:cursorchanged', (e, rawSelection) => {
                  TrackingService.recordEvent(scope.trackCategory, `${actionPrefix}TypeaheadInteraction`, 'cursorchanged');
                  lastCursorItem = rawSelection;
                });

                      // Tie into typeahead DOM events
                typeaheadElem.on('keydown.tt', (e) => {
                  const key = keyCodeMap[e.which || e.keyCode];

                  if (key && scope.events[`${key}Keyed`]) {
                    scope.$apply(() => {
                      scope.events[`${key}Keyed`].call(typeaheadElem, e, lastCursorItem, lastQuery);
                      TrackingService.recordEvent(scope.trackCategory, `${actionPrefix}TypeaheadInteraction`, key);
                    });
                  }
                });

                scope.$on('twitter-typeahead:submit', (e, key) => {
                  scope.events[`${key}Keyed`].call(typeaheadElem, e, lastCursorItem, lastQuery);
                  TrackingService.recordEvent(scope.trackCategory, `${actionPrefix}TypeaheadInteraction`, 'submit');
                });

                scope.$on('twitter-typeahead:reset', () => {
                  const typeaheadEl = angular.element(element);
                  lastCursorItem = null;
                  typeaheadEl.typeahead('val', null);
                  typeaheadEl.typeahead('close');
                });

                      /**
                       * This option enables a hack to position the typeahead dropdown as "fixed" instead of absolute.
                       * We use this on Influencers Sidebar to keep the typeahead dropdown from
                       * being clipped by the 'overflow-y: scroll' contianer it lives within.
                       */
                if (scope.fixedScrollBind) {
                          // Element to position the dropdown based on--this is the typeahead input box.
                  const offsetSource = typeaheadElem.parent();
                          // Element that needs to be positioned (the results dropdown)
                  const positionTarget = offsetSource.find('.tt-dropdown-menu');

                          // Method to update the position of the dropdown based on the screen position of the input box
                  const updateFixedPosition = function() {
                              // getBoudingClientRect gives us positions relative to the current viewport,
                              // so we can use it to set fixed positions
                    const rect = offsetSource[0].getBoundingClientRect();

                              // Top of the dropdown should be just past the input box--so top + height.
                    positionTarget[0].style.top = `${rect.top + rect.height}px`;
                    positionTarget[0].style.left = `${rect.left}px`;
                  };

                  const registerListeners = function() {
                    updateFixedPosition();
                    angular.element(scope.fixedScrollBind).on('scroll', updateFixedPosition);
                    angular.element(window).on('resize', updateFixedPosition);
                  };
                  const unregisterListeners = function() {
                    angular.element(scope.fixedScrollBind).off('scroll', updateFixedPosition);
                    angular.element(window).off('resize', updateFixedPosition);
                  };

                          // Set the dropdown to use fixed positioning.
                          // Typeahead will override this if we try to set it with just a CSS class, so we use inline.
                  positionTarget[0].style.position = 'fixed';

                          // Register listeners to start tracking scrolls and update positioning of the dropdown
                          // as the dropdown opens/closes.
                  typeaheadElem.on('typeahead:opened', registerListeners);
                  typeaheadElem.on('typeahead:closed', unregisterListeners);

                  scope.$on('$destroy', () => {
                    unregisterListeners();
                    typeaheadElem.off('typeahead:opened', registerListeners);
                    typeaheadElem.off('typeahead:closed', unregisterListeners);
                  });

                          // For good measure, in case the dropdown is somehow already opened.
                  updateFixedPosition();
                }
              };

                  // Create a list of promises, one for each template
                  // If the template is just a string, create a pre-resolved promise
                  // If the template is a url, request it and return the promise
              const promises = _(scope.templates).map((obj, key) => {
                let deferred;

                if (obj.url) {
                  deferred = $q.defer();
                  $http.get(obj.url).then((result) => {
                    deferred.resolve({
                      type: key,
                      template: result.data,
                    });
                  });

                  return deferred.promise;
                } else {
                  return $q.when({
                    type: key,
                    template: obj.html,
                  });
                }
              });

                  // Combine the list of promise in a single promise and bind the typeahead
                  // when all promises have returned
              $q.all(promises).then(bindTypeahead);
            },
          };
        }]);
